/**
 * GroupHeader
 *
 * Header row for a single group in the GroupTree. Shows:
 *   - expand/collapse chevron
 *   - group name (click → inline rename; Enter/blur to save)
 *   - agent count badge
 *   - "⋮" overflow menu: Rename / New subgroup / Delete
 *
 * All mutations go through the hooks in `useGroups.ts`. The component owns
 * only the local "editing?" UI state.
 */

import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useCreateSubgroup, useDeleteGroup, useUpdateGroup } from '@/hooks/useGroups';
import { useGroupsStore } from '@/store/groupsStore';
import type { UserGroup } from '@/types/groups';

interface GroupHeaderProps {
  group: UserGroup;
  depth: number;
}

export const GroupHeader: React.FC<GroupHeaderProps> = ({ group, depth }) => {
  const isCollapsed = useGroupsStore((s) => s.isCollapsed(group.id));
  const toggleCollapse = useGroupsStore((s) => s.toggleCollapse);

  const updateGroup = useUpdateGroup(group.id);
  const deleteGroup = useDeleteGroup();
  const createSubgroup = useCreateSubgroup(group.id);

  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(group.name);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const startEditing = () => {
    setDraftName(group.name);
    setEditing(true);
    setMenuAnchor(null);
  };

  const commitRename = () => {
    const next = draftName.trim();
    if (next && next !== group.name) {
      updateGroup.mutate({ name: next });
    }
    setEditing(false);
  };

  const handleNewSubgroup = () => {
    const name = window.prompt('Nombre del subgrupo:');
    if (!name || !name.trim()) return;
    createSubgroup.mutate({ name: name.trim() });
    setMenuAnchor(null);
  };

  const handleDelete = () => {
    const ok = window.confirm(
      `¿Eliminar el grupo "${group.name}"? Sus subgrupos se borran y los agentes asignados vuelven a "(Sin grupo)".`
    );
    if (!ok) return;
    deleteGroup.mutate(group.id);
    setMenuAnchor(null);
  };

  const paddingLeft = depth * 2;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        pl: `${paddingLeft}px`,
        py: 0.75,
      }}
      data-testid={`group-header-${group.id}`}
    >
      <IconButton
        size="small"
        onClick={() => toggleCollapse(group.id)}
        aria-label={isCollapsed ? 'Expandir' : 'Colapsar'}
        data-testid={`group-toggle-${group.id}`}
      >
        {isCollapsed ? <ChevronRightIcon /> : <ExpandMoreIcon />}
      </IconButton>

      {group.color && (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: group.color,
            flexShrink: 0,
          }}
        />
      )}

      {editing ? (
        <TextField
          size="small"
          value={draftName}
          autoFocus
          onChange={(e) => setDraftName(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            if (e.key === 'Escape') setEditing(false);
          }}
          sx={{ flex: 1, maxWidth: 320 }}
          data-testid={`group-name-input-${group.id}`}
        />
      ) : (
        <Box
          onClick={startEditing}
          sx={{
            flex: 1,
            fontWeight: 600,
            cursor: 'text',
            userSelect: 'none',
            minWidth: 0,
          }}
          title="Click para renombrar"
          data-testid={`group-name-${group.id}`}
        >
          {group.name}
          {updateGroup.isPending && (
            <CircularProgress size={12} sx={{ ml: 1, verticalAlign: 'middle' }} />
          )}
        </Box>
      )}

      {group.agentCount > 0 && (
        <Chip
          label={group.agentCount}
          size="small"
          sx={{ height: 20, fontSize: '0.7rem' }}
          data-testid={`group-agent-count-${group.id}`}
        />
      )}

      <IconButton
        size="small"
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        aria-label="Más acciones"
        data-testid={`group-menu-${group.id}`}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={startEditing} data-testid={`group-menu-rename-${group.id}`}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Renombrar</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleNewSubgroup} data-testid={`group-menu-new-subgroup-${group.id}`}>
          <ListItemIcon>
            <CreateNewFolderIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Nuevo subgrupo</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete} data-testid={`group-menu-delete-${group.id}`}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Eliminar grupo</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};
